import { createException, RegistryErrorCode } from '../errors/RegistryError';
import {
  IComponentManifest,
  IComponentLoader,
  IComponentBundleLoader,
  IComponentBundleStructure,
} from '../registry.types';
import {
  filterDependencyAssets,
  filterComponentAssets,
  filterPartAssets,
} from '../toolbox/assets';

const withEagerPart = (component: IComponentManifest) => (name: string) =>
  !!component.parts[name].eager;

const withUniqNames = (name: string, index: number, self: Array<string>) =>
  !self.includes(name, index + 1);

export function createComponentLoader<
  TComponentModel,
  TParts extends Extract<keyof TComponentModel, string> = never,
  TComponentManifest extends IComponentManifest = IComponentManifest<TParts>,
>({
  loadBundle,
  component,
  statics = {},
}: {
  loadBundle: IComponentBundleLoader;
  component: TComponentManifest;
  statics: Record<string, any>;
}) {
  const loader: IComponentLoader<TComponentModel, TParts> = async part => {
    if (part && !Object.keys(component.parts).length) {
      throw createException(
        'Loading component part is not available. It is only possible to load the whole component',
        RegistryErrorCode.IncorrectLoaderArguments,
      ).withComponent(component.name);
    }

    let url: string | undefined;
    let id: string | undefined;

    if (part) {
      url = component.parts[part as string]?.url;
      id = component.parts[part as string]?.id;
    } else {
      url = component.url;
      id = component.id;
    }

    if (!url || !id) {
      throw createException(
        'It is not possible load the component',
        RegistryErrorCode.IncorrectLoaderArguments,
      ).withComponent(component.name, part, Object.keys(component.parts));
    }

    const tasks: Array<
      Promise<IComponentBundleStructure<TComponentModel> | null>
    > = [];

    if (component.deps) {
      await Promise.all([
        ...component.deps.map(dep =>
          loadBundle({
            url: dep,
          }),
        ),
        loadBundle({
          assets: filterDependencyAssets(component.assets),
        }),
      ]);
    }

    if (!part) {
      tasks.push(
        loadBundle<TComponentModel>({
          id,
          url,
          assets: filterComponentAssets(component.assets),
        }),
      );
    }

    if (part) {
      const componentPart = component.parts[part as string];

      tasks.push(
        loadBundle<TComponentModel>({
          id: componentPart.id,
          url: componentPart.url,
          assets: filterPartAssets(component.assets, part),
        }),
      );
    } else {
      const eagerRequests = Object.keys(component.parts)
        /**
         * get eager parts
         */
        .filter(withEagerPart(component))
        /**
         * get only uniq parts (multiple parts may lead to the same batch bundle)
         */
        .filter(withUniqNames);

      eagerRequests.forEach(eagerPartName => {
        const eagerPart = component.parts[eagerPartName];

        tasks.push(
          loadBundle<TComponentModel>({
            id: eagerPart.id,
            url: eagerPart.url,
            assets: filterPartAssets(component.assets, eagerPartName),
          }),
        );
      });
    }

    const bundles = await Promise.all(tasks);

    if (bundles.some(bundle => !bundle || !bundle.components)) {
      throw createException(
        'Incorrect bundle. Bundle does contain "components" export',
        RegistryErrorCode.IncorrectBundleExport,
      )
        .withComponent(component.name, part)
        .withUrl(url);
    }

    const model = Object.assign(
      {},
      ...bundles.map(bundle =>
        bundle ? bundle.components[component.name] : {},
      ),
    );

    if (!model) {
      throw createException(
        'Incorrect component bundle. Component model does not exist',
        RegistryErrorCode.IncorrectBundleExport,
      )
        .withComponent(component.name, part)
        .withUrl(url)
        .withHint('Probably library was bundled in a wrong way');
    }

    if ((model as any) === 1) {
      throw createException(
        'Incorrect bundle. Wrong component model',
        RegistryErrorCode.IncorrectBundleExport,
      )
        .withComponent(component.name, part)
        .withUrl(url)
        .withHint("Probably library's webpack runtime was not loaded");
    }

    // @ts-ignore
    return part ? model[part] : model;
  };

  loader.isPartExist = part => {
    return component.parts.hasOwnProperty(part);
  };

  loader.assets = component.assets;
  loader.statics = statics;

  return loader;
}
